import {
  MessageAttributeValue,
  SendMessageCommand,
  SQSClient,
} from "@aws-sdk/client-sqs"
import { DbTransaction } from "@tc/middy-decorators"
import { eq, and } from "drizzle-orm"
import { partyAgency, partyUserRoleConstraint } from "@tc/drizzle-v2"

const sqs_client = new SQSClient({})
const queue_url = process.env["AGENCY_QUEUE_URL"]

export type Address = {
  physical_address?: string | null
  physical_city?: string | null
  physical_state?: string | null
  physical_country_code?: string | null
}

export type AgencyDoc = {
  id: string
  identifier: string
  agency_type: string
  display_name: string | null
  address?: Address
  source_system_id?: string | null
  accessible_by: string[]
}

export const getAgencyData = async (
  tx: DbTransaction,
  id: string,
): Promise<AgencyDoc | undefined> => {
  const [agencyItem] = await tx
    .select({
      id: partyAgency.id,
      identifier: partyAgency.identifier,
      agency_type: partyAgency.agencyType,
      display_name: partyAgency.displayName,
      physical_address: partyAgency.physicalAddress,
      physical_city: partyAgency.city,
      physical_state: partyAgency.state,
      physical_country_code: partyAgency.countryCode,
      source_system_id: partyAgency.sourceSystemId,
    })
    .from(partyAgency)
    .where(eq(partyAgency.id, id))

  if (!agencyItem) {
    console.error(`Agency not found for id: ${id}`)
    return undefined
  }

  const userAccess = await tx
    .select({ userId: partyUserRoleConstraint.userId })
    .from(partyUserRoleConstraint)
    .where(
      and(
        eq(partyUserRoleConstraint.agencyId, id),
        eq(partyUserRoleConstraint.constraintType, "AGENCY"),
        eq(partyUserRoleConstraint.active, true),
      ),
    )

  return {
    id: agencyItem.id,
    identifier: agencyItem.identifier,
    agency_type: agencyItem.agency_type,
    display_name: agencyItem.display_name ?? "",
    address: {
      physical_address: agencyItem.physical_address ?? "",
      physical_city: agencyItem.physical_city ?? "",
      physical_state: agencyItem.physical_state ?? "",
      physical_country_code: agencyItem.physical_country_code ?? "",
    },
    source_system_id: agencyItem.source_system_id ?? "",
    accessible_by:
      userAccess.length > 0 ? userAccess.map((u) => u.userId) : ["*"],
  }
}

export const saveAgencyToSearch = async (
  tx: DbTransaction,
  agencyId: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const doc = await getAgencyData(tx, agencyId)
  if (!doc || Object.keys(doc).length === 0) {
    console.error(`Failed to prepare document for id: ${agencyId}`)
    return
  }

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: {
      StringValue: collectionName,
      DataType: "String",
    },
  }
  const command = new SendMessageCommand({
    QueueUrl: queue_url,
    MessageBody: JSON.stringify(doc),
    MessageAttributes: attributes,
  })

  await sqs_client.send(command)
}
